import styled from 'styled-components';
import React, {FC, useState, useEffect} from 'react';
import {Link} from 'gatsby';

import {convertHexToRGBA} from '../../../../../lib/color-utils';
import {useWindowScroll} from '../../../../../lib/hooks/useWindowScroll';
import {
  fontWeights,
  mediaBreakpointDown,
  mediaQueryMixins,
  menuBreakpoint,
  palette,
  transition,
  desktopWideBreakpoint
} from '../../../../../lib/styleVariables';
import {PrimaryLink, SecondaryLink} from '../../../../molecules/buttonsR4';
import {Icon} from '../../../../molecules/icon';
import {HeaderMenuProps} from '../headerMenuTypes';
import {StyledSubMenuWrapper as HeaderSubMenuWrapperDiv} from '../headerSubMenu';
import {HeaderExpandMobileMenu} from './expandMobileMenu';
import {HeaderLeftNav, StyledLeftMenuButtonText} from './leftNav';
import {fontSizesMixinsR4, paletteR4} from '../../../../../lib/styleVariablesR4';
import {useIsServer} from '../../../../../lib/hooks/useIsServer';
import {getTopBarHeight} from '../../../../molecules/topbarR4';
import {headerHeight} from '../headerVariables';
import {getWindowHeight} from '../../../../../lib/hooks/useWindowWidth';

interface MainWrapperProps {
  isMenuOpen: boolean;
  backgroundColor: string;
  boxShadow: string;
  isScrolled: boolean;
  $css?: string;
  topBarHeight: number;
  windowInnerHeight: number;
}

interface StyledLeftMenuButtonProps {
  $css?: string;
  isActive: boolean;
}

const MAX_SCROLL_DISTANCE = 120;
const LOGO_APPEAR_THRESHOLD = 250;

const StyledMainWrapper = styled.div<MainWrapperProps>`
  top: -1px; /* -1px is used to prevent browser render bug for sticky position */
  position: sticky;
  z-index: 2;
  width: 100%;
  background-color: ${props => props.backgroundColor};
  box-shadow: ${props => props.boxShadow};
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background-color 0.3s, box-shadow 0.3s;

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    margin: 0;
    width: 100%;
    bottom: ${props => (props.isMenuOpen ? '0' : 'initial')};
    height: ${props =>
    (props.isMenuOpen
      ? `${props.windowInnerHeight - props.topBarHeight + 1}px`
      : `${headerHeight.mobile + 1}px`)}; /* 1px is added to compansate -1px sticky position fix */
  }

  [data-theme='dark'] & {
    ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
      background: ${palette.black.base};
    }
  }

  ${props => props.$css}
`;

const StyledHeaderMenuWrapper = styled.div<{isMenuOpen: boolean; $css?: string}>`
  height: ${headerHeight.desktop + 1}px; /* 1px is added to compansate -1px sticky position fix */
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 2;
  flex-shrink: 0;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    height: ${headerHeight.mobile + 1}px; /* 1px is added to compansate -1px sticky position fix */
  }
  ${props => !props.isMenuOpen && props.$css}
`;

const StyledHeaderMenu = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  max-width: 1420px;
  margin: 0 auto;
  padding: 0 40px 0 60px;
  width: 100%;

  ${mediaBreakpointDown('desktop')} {
    padding: 0 24px;
  }
`;

const StyledAnimatedLogo = styled(Icon)<{isVisible: boolean}>`
  margin-right: 51px;

  ${mediaQueryMixins.minWidth(`${menuBreakpoint + 1}px`)} {
    transition: ${transition.fast};
    opacity: ${props => (props.isVisible ? 1 : 0)};
    visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  }

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    max-height: 40px;
  }
`;

const StyledMenuItems = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    display: none;
  }
  ${mediaQueryMixins.between(`${menuBreakpoint}px`, `${desktopWideBreakpoint}px`)} {
    margin-left: 0;
  }
`;

const StyledRightMenuContainer = styled.div`
  align-items: center;
  display: flex;
`;

const commonHeaderTextStyles = `
  ${fontSizesMixinsR4.h5()};
  font-weight: ${fontWeights.semibold};
  color: ${paletteR4.black.base};
`;

export const StyledLeftMenuButton = styled.a<StyledLeftMenuButtonProps>`
  display: inline-block;
  text-decoration: none;
  background-color: transparent;
  color: ${palette.black.base};
  cursor: pointer;
  transition: background-color 0.3s;
  ${commonHeaderTextStyles};

  [data-theme='dark'] & {
    color: ${palette.white.highlight};
  }

  &:hover {
    ${HeaderSubMenuWrapperDiv} {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
    ${StyledLeftMenuButtonText} {
      &::before,
      &::after {
        visibility: visible;
        opacity: 1;
      }
    }
  }

  ${StyledLeftMenuButtonText} {
    &::after {
      visibility: ${props => (props.isActive ? 'visible' : 'hidden')};
      opacity: ${props => (props.isActive ? '1' : '0')};
    }
  }

  &:not(:last-child) {
    margin-right: 40px;
  }

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    padding: 8px;
    border-radius: 0px;
    background: transparent;
  }
  ${props => props.$css}
`;

export const rightMenuButtonStyle = `
  margin-left: 16px;
`;

export const StyledRightMenuTertiaryButton = styled.a`
  display: inline-block;
  text-decoration: none;
  margin-right: 8px;
  ${commonHeaderTextStyles};

  [data-theme='dark'] & {
    color: ${palette.white.highlight};
  }

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    padding: 8px;
    margin: 0px;
    margin-bottom: 16px;
  }
  transition: ${transition.fast};
`;

const StyledMobileMenuIcon = styled.div`
  display: none;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    display: flex;
    justify-content: flex-end;
  }
`;

const StyledHeaderLeftContainer = styled.div`
  margin-right: 45px;
  ${mediaBreakpointDown('desktop')} {
    margin-right: 0;
  }
`;

const StyledPrimaryLink = styled(PrimaryLink)`
  ${rightMenuButtonStyle}
  [data-theme='dark'] & {
    background: ${palette.white.highlight};
    border: 2px solid ${palette.white.highlight};
    color: ${palette.black.base};
    &:hover {
      background: transparent;
      color: ${palette.white.highlight};
    }
  }
`;

const StyledSecondaryLink = styled(SecondaryLink)<{backgroundColor: string}>`
  ${rightMenuButtonStyle}
  background-color: ${props => props.backgroundColor};
  [data-theme='dark'] & {
    border: 2px solid ${palette.white.highlight};
    background: transparent;
    color: ${palette.white.highlight};
    &:hover {
      background: ${palette.white.highlight};
      color: ${palette.black.base};
    }
  }
`;

export const HeaderMenu: FC<HeaderMenuProps> = props => {
  const [topBarHeight, setTopBarHeight] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {scrollY} = useWindowScroll();
  const isServer = useIsServer();

  useEffect(() => {
    // Page has to be scrollable again if the location changes
    document.body.style.overflow = 'initial';
  }, [props.locationPath]);

  const onMenuIconClick = async () => {
    setTopBarHeight(await getTopBarHeight());

    // user shouldn't be able to scroll on body when mobile menu is open
    document.body.style.overflow = isMenuOpen ? 'initial' : 'hidden';
    setIsMenuOpen(!isMenuOpen);
  };

  const isScrolled = scrollY > MAX_SCROLL_DISTANCE;
  const isLogoVisible = scrollY > LOGO_APPEAR_THRESHOLD;

  const defaultBackgroundColor = props.isDark ? palette.black.base : paletteR4.white.base;
  const defaultBoxShadow = `0px 4px 16px ${convertHexToRGBA(palette.black.base, props.isDark ? 0.32 : 0.08)}`;

  const getInitialHeaderBackgroundColor = () => {
    if (props.hasBackgroundColorAnimation)
      return 'transparent';
    if (props.isHighlighted)
      return paletteR4.gray.highlight!;

    return defaultBackgroundColor;
  };

  const getHeaderBackgroundColor = () => {
    if (isServer || !(isScrolled || isMenuOpen))
      return getInitialHeaderBackgroundColor();

    return defaultBackgroundColor;
  };

  const getHeaderBoxShadow = () => {
    if (isServer || !isScrolled)
      return 'none';

    return defaultBoxShadow;
  };

  const headerBackgroundColor = getHeaderBackgroundColor();
  const headerBoxShadow = getHeaderBoxShadow();

  const windowHeight = getWindowHeight();

  return (
    <StyledMainWrapper
      backgroundColor={headerBackgroundColor}
      boxShadow={headerBoxShadow}
      className={props.className}
      isMenuOpen={isMenuOpen}
      isScrolled={isScrolled}
      $css={props.mainWrapperCss}
      topBarHeight={topBarHeight}
      windowInnerHeight={windowHeight}
    >
      <StyledHeaderMenuWrapper isMenuOpen={isMenuOpen} $css={props.menuWrapperCss}>
        <StyledHeaderMenu>
          <Link to="/">
            <StyledAnimatedLogo
              name={props.isDark ? 'r4HomeLogoWhiteSmall' : 'r4HomeLogoSmall'}
              isVisible={props.hasAnimatedLogo ? isLogoVisible : true}
              useR4
            />
          </Link>
          <StyledMobileMenuIcon>
            <Icon name={isMenuOpen ? 'exit' : 'hamburgerMenu'} onClick={onMenuIconClick} size={24} useR4 />
          </StyledMobileMenuIcon>
          <StyledMenuItems>
            <StyledHeaderLeftContainer>
              <HeaderLeftNav
                isHeaderHighlighted={props.isHighlighted}
                isScrolled={isScrolled}
                leftNav={props.leftNav}
                locationPath={props.locationPath}
              />
            </StyledHeaderLeftContainer>
            <StyledRightMenuContainer>
              {props.rightNav.map(item => (
                <StyledRightMenuTertiaryButton href={item.href} key={item.name}>
                  {item.name}
                </StyledRightMenuTertiaryButton>
              ))}
              {!!props.secondaryItem.name && !!props.secondaryItem.href && (
                <StyledSecondaryLink href={props.secondaryItem.href} backgroundColor={headerBackgroundColor}>
                  {props.secondaryItem.name}
                </StyledSecondaryLink>
              )}
              <StyledPrimaryLink href={props.primaryItem.href}>{props.primaryItem.name}</StyledPrimaryLink>
            </StyledRightMenuContainer>
          </StyledMenuItems>
        </StyledHeaderMenu>
      </StyledHeaderMenuWrapper>
      {isMenuOpen && (
        <HeaderExpandMobileMenu
          leftNav={props.leftNav}
          rightNav={props.rightNav}
          primaryItem={props.primaryItem}
          secondaryItem={props.secondaryItem}
          locationPath={props.locationPath}
        />
      )}
    </StyledMainWrapper>
  );
};
